'use client';

import { useEffect, useState } from "react";
import useContent from 'store/content';
import { matchColorsBanner } from "utils/colors";
import { useAppCache, type SyncProgress } from "../Hooks/useAppCache";
import { SCPWABannerTitle } from "../styles";
import { useOnlineStatus } from "../../../hooks/useOnlineStatus";
import {
    SCBannerTopRow,
    SCClearButton,
    SCIconWrapper,
    SCProgressFiller,
    SCProgressLabels,
    SCProgressTrack,
    SCProgressWrapper,
    SCRefreshButton,
    SCStatusGroup,
    SCStatusText
} from './styles';
import { useTranslation } from 'react-i18next'

// Fixed display order and labels for the per-channel bars. Each maps to a
// progress channel broadcast by the service worker.
const CHANNELS: { key: string; label: string }[] = [
    { key: 'app', label: 'App' },
    { key: 'images', label: 'Pages & images' },
];

export default function CacheBanner() {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);
    const content = useContent();
    const { appInfos, themeInfos } = content.get.state.app;
    const online = useOnlineStatus();

    const { contentStatus, syncChannels = {}, handleClearCache } = useAppCache();

    const pwaBanner = appInfos?.app_settings?.pwa_banner;

    const bgColor = themeInfos?.details?.colors?.banner || '#ffffff';
    const textColor = pwaBanner?.text_color || matchColorsBanner(bgColor);

    const status = contentStatus?.trim();
    const isUnavailable = status === 'No service worker' || status === 'PWA not supported';
    const isReady = status === 'Synchronized';

    // True once any channel has reported progress this session. Used to decide
    // what an *absent* channel should show.
    const syncing = Object.keys(syncChannels).length > 0;

    // Each bar reflects its own channel honestly: its denominator is reserved up
    // front in the service worker, so completed/total only ever climbs and a
    // channel reaching 100% genuinely means that channel is fully cached. For a
    // channel that hasn't reported yet: show 0% while another channel is actively
    // syncing (its work is still pending — e.g. the app shell finishes caching
    // before the pages/images messages arrive), otherwise fall back to 100% when
    // everything is already Synchronized.
    const channelPercentage = (progress?: SyncProgress) => {
        const total = progress?.total ?? 0;
        const completed = progress?.completed ?? 0;
        if (total > 0) return Math.min(Math.max(Math.round((completed / total) * 100), 0), 100);
        if (syncing) return 0;
        return isReady ? 100 : 0;
    };

    useEffect(() => { setMounted(true); }, []);

    if (!mounted) return <div>Loading...</div>;

    const renderStatusMarker = () => {
        if (isUnavailable) {
            return (
                <SCIconWrapper $textColor={textColor} $isAnimated={false}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
                    </svg>
                </SCIconWrapper>
            );
        }

        if (isReady) {
            return (
                <SCIconWrapper $textColor={textColor} $isAnimated={false}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                </SCIconWrapper>
            );
        }

        if (status === 'Out of date') {
            return (
                <SCRefreshButton $textColor={textColor} title="Click to sync update" onClick={() => {}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/>
                    </svg>
                </SCRefreshButton>
            );
        }

        return (
            <SCIconWrapper $textColor={textColor} $isAnimated={true}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/>
                </svg>
            </SCIconWrapper>
        );
    };

    return (
      <div>
        <SCBannerTopRow>
          <SCPWABannerTitle $themeInfos={themeInfos} $isOpen={true}>
            PWA status
          </SCPWABannerTitle>
        </SCBannerTopRow>

        <SCProgressWrapper>
          <SCStatusGroup>
            {renderStatusMarker()}
            <span>
              sync state: <SCStatusText>{contentStatus}</SCStatusText>
            </span>
          </SCStatusGroup>
        </SCProgressWrapper>

        {CHANNELS.map(({ key, label }) => {
          const progress = channelPercentage(syncChannels[key])
          return (
            <SCProgressWrapper key={key}>
              <SCProgressLabels>
                <span>{label}</span>
                <span>{progress}%</span>
              </SCProgressLabels>
              <SCProgressTrack>
                <SCProgressFiller $progress={progress} />
              </SCProgressTrack>
            </SCProgressWrapper>
          )
        })}
        {online && (
          <SCClearButton style={{marginTop: '1rem'}} $textColor={textColor} onClick={handleClearCache}>
            {t("pwaBanner.restartCache")}
          </SCClearButton>
        )}
      </div>
    )
}